import { ChevronLeft } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { ApiError, getJSON } from '@/lib/api'

type DirectoryField = {
  key: string
  label: string
  value: string | number | string[] | { thumbnail_url: string; original_url?: string }
}

type MemberProfileResponse = {
  member_directory: {
    member?: {
      slug: string
      name: string
      fields: DirectoryField[]
    }
  }
}

export function MemberPortalMemberProfile({ slug }: { slug: string }) {
  const profile = useQuery<MemberProfileResponse>({
    queryKey: ['member-portal-directory-member', slug],
    queryFn: () => getJSON(`/api/v1/member_directory/${encodeURIComponent(slug ?? '')}`),
    enabled: Boolean(slug),
    retry: false,
  })

  if (profile.isLoading) {
    return (
      <p className="mt-6 text-body-sm text-muted-foreground" role="status">
        Loading member profile…
      </p>
    )
  }

  if (profile.error instanceof ApiError && profile.error.status === 404) {
    return <Alert className="mt-6">We couldn’t find that member.</Alert>
  }

  if (profile.isError || !profile.data?.member_directory.member) {
    return <Alert className="mt-6">The member profile is unavailable. Please try again.</Alert>
  }

  const member = profile.data.member_directory.member
  return <MemberProfileView member={member} />
}

function MemberProfileView({
  member,
}: {
  member: NonNullable<MemberProfileResponse['member_directory']['member']>
}) {
  const field = (key: string) => member.fields.find((item) => item.key === key)
  const photo = field('profile_photo')?.value
  const photoUrl =
    typeof photo === 'object' && !Array.isArray(photo) ? photo.original_url : undefined
  const level = field('membership_level')?.value
  const organizations = field('organization_affiliations')?.value
  const organizationText = Array.isArray(organizations) ? organizations.join(', ') : organizations
  const factRows = member.fields.filter(
    ({ key, value }) =>
      ![
        'first_name',
        'last_name',
        'membership_level',
        'organization_affiliations',
        'profile_photo',
      ].includes(key) && value !== '',
  )

  return (
    <div className="w-full space-y-6">
      <Link
        to="/portal/directory"
        className="inline-flex items-center gap-1.5 text-body-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="size-4" aria-hidden="true" />
        Directory
      </Link>

      <div className="flex items-center gap-4">
        {photoUrl ? (
          <img src={photoUrl} alt="" className="size-24 shrink-0 rounded-md object-cover" />
        ) : (
          <div className="flex size-24 shrink-0 items-center justify-center rounded-md bg-muted">
            <span className="text-heading-sm font-medium text-muted-foreground">
              {initials(member.name)}
            </span>
          </div>
        )}
        <div className="min-w-0 space-y-0.5 leading-snug">
          {level !== undefined && (
            <p className="text-caption font-medium text-primary">{String(level)}</p>
          )}
          <h1 className="text-subheading font-semibold text-foreground">{member.name}</h1>
          {organizationText && (
            <p className="text-body-sm text-muted-foreground">{String(organizationText)}</p>
          )}
        </div>
      </div>

      {factRows.length > 0 && (
        <div className="overflow-hidden rounded-md border border-border bg-card">
          {factRows.map((row) => (
            <FactRow key={row.key} row={row} />
          ))}
        </div>
      )}
    </div>
  )
}

function FactRow({ row }: { row: DirectoryField }) {
  return (
    <div className="flex gap-4 border-b border-border px-4 py-2 last:border-b-0">
      <span className="w-40 shrink-0 text-caption text-muted-foreground">{row.label}</span>
      <span className="min-w-0 flex-1 text-body-sm text-foreground">
        <FactValue row={row} />
      </span>
    </div>
  )
}

function FactValue({ row }: { row: DirectoryField }) {
  if (row.key === 'email') {
    return (
      <a href={`mailto:${String(row.value)}`} className="text-primary hover:underline">
        {String(row.value)}
      </a>
    )
  }
  return Array.isArray(row.value) ? row.value.join(', ') : String(row.value)
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}
